import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'

import { QRCodeRegenerationStatus } from '@/utils/qr-code/qr-code-regeneration.constant'

interface DownloadAllResponse {
  process_id: string
  status: string
  message: string
}

interface CheckDownloadStatusResponse {
  status: string
  message?: string
}

interface UseDownloadAllQRCodesReturn {
  downloadAll: (filename?: string) => Promise<void>
  isLoading: boolean
  error: Nullable<string>
}

const POLLING_INTERVAL = 5000

export function useDownloadAllQRCodes(): UseDownloadAllQRCodesReturn {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const { handleError } = useErrorHandler()
  const handleErrorRef = useRef(handleError)
  const shouldContinuePollingRef = useRef<boolean>(false)
  const filenameRef = useRef<string>(t('qrCode.downloadAllFilename'))

  useEffect(() => {
    handleErrorRef.current = handleError
  }, [handleError])

  const downloadFile = useCallback(
    async (processId: string, filename: string) => {
      try {
        const response = await apiClient.get('/qr-codes/download/qr-zip', {
          params: {
            process_id: processId,
          },
          responseType: 'blob',
        })

        const blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        setError(handleErrorRef.current(err))
        throw err
      }
    },
    []
  )

  const checkStatus = useCallback(
    async (processId: string): Promise<CheckDownloadStatusResponse> => {
      try {
        const response = await apiClient.get<CheckDownloadStatusResponse>(
          '/qr-codes/check-status-download',
          {
            params: {
              process_id: processId,
              wait_seconds: POLLING_INTERVAL / 1000,
            },
          }
        )
        return response.data
      } catch (err) {
        setError(handleErrorRef.current(err))
        return { status: '' }
      }
    },
    []
  )

  const pollStatus = useCallback(
    async (processId: string): Promise<void> => {
      if (!shouldContinuePollingRef.current) {
        return
      }

      const statusResponse = await checkStatus(processId)
      const normalizedStatus = statusResponse.status.toLowerCase()

      if (normalizedStatus === QRCodeRegenerationStatus.DONE) {
        shouldContinuePollingRef.current = false
        setIsLoading(false)
        await downloadFile(processId, filenameRef.current)
      } else if (normalizedStatus === QRCodeRegenerationStatus.IN_PROGRESS) {
        if (shouldContinuePollingRef.current) {
          await pollStatus(processId)
        }
      } else {
        shouldContinuePollingRef.current = false
        setIsLoading(false)
      }
    },
    [checkStatus, downloadFile]
  )

  const downloadAll = useCallback(
    async (filename?: string) => {
      setIsLoading(true)
      setError(null)
      shouldContinuePollingRef.current = false
      filenameRef.current = filename || t('qrCode.downloadAllFilename')

      try {
        const response = await apiClient.post<DownloadAllResponse>(
          '/qr-codes/download/all'
        )
        const { process_id, status } = response.data
        const normalizedStatus = status.toLowerCase()

        if (
          normalizedStatus === QRCodeRegenerationStatus.IN_PROGRESS &&
          process_id
        ) {
          shouldContinuePollingRef.current = true
          await pollStatus(process_id)
        } else if (
          normalizedStatus === QRCodeRegenerationStatus.DONE &&
          process_id
        ) {
          await downloadFile(process_id, filenameRef.current)
          setIsLoading(false)
        } else {
          setIsLoading(false)
        }
      } catch (err) {
        setError(handleErrorRef.current(err))
        setIsLoading(false)
        shouldContinuePollingRef.current = false
        throw err
      }
    },
    [pollStatus, downloadFile, t]
  )

  return {
    downloadAll,
    isLoading,
    error,
  }
}
